/**
 * Session Summary Data — Confetti particles, types, and animated counter hook.
 *
 * Extracted from SessionSummary.tsx during CL-1 s65.
 * Pure data + hook — keeps the main component focused on layout.
 */

import { useEffect } from 'react';
import { useMotionValue, useTransform, animate } from 'framer-motion';

// ── Types ────────────────────────────────────────────────────

export interface SessionResult {
  questionId: string;
  isCorrect: boolean;
  familyId?: string;
  questionType: string;
  errorType?: string;
  timeMs?: number;
}
export interface ConfettiDot {
  top: string;
  left?: string;
  right?: string;
  color: string;
  size: string;
  delay: number;
}

// ── Confetti Particle Positions ──────────────────────────────

export const CONFETTI: ConfettiDot[] = [{
  top: '8%',
  left: '12%',
  color: 'bg-yellow-400',
  size: 'w-2.5 h-2.5',
  delay: 0.4
}, {
  top: '15%',
  right: '10%',
  color: 'bg-emerald-400',
  size: 'w-2 h-2',
  delay: 0.55
}, {
  top: '25%',
  left: '6%',
  color: 'bg-indigo-400',
  size: 'w-2 h-2',
  delay: 0.3
}, {
  top: '30%',
  right: '8%',
  color: 'bg-rose-300',
  size: 'w-1.5 h-1.5',
  delay: 0.65
}, {
  top: '60%',
  left: '8%',
  color: 'bg-amber-300',
  size: 'w-2.5 h-2.5',
  delay: 0.5
}, {
  top: '70%',
  right: '6%',
  color: 'bg-sky-400',
  size: 'w-2 h-2',
  delay: 0.45
}];

// ── Heading / Subtitle Copy ──────────────────────────────────

export function getSummaryHeading(percentage: number): {
  heading: string;
  subtitle: string;
} {
  const isPerfect = percentage === 100;
  const isGreat = percentage >= 80;
  const isGood = percentage >= 50;
  const heading = isPerfect ? 'Perfecte sessie! 🌟' : isGreat ? 'Lekker geoefend! 💪' : isGood ? 'Goed bezig! 📚' : 'Geoefend! 📚';
  const subtitle = isPerfect ? 'Geen enkele fout — wat knap!' : isGreat ? 'Je hebt er echt voor gegaan!' : isGood ? 'Elke sessie maakt je sterker.' : 'Oefening baart kunst — ga zo door!';
  return {
    heading,
    subtitle
  };
}

// ── Animated Counter Hook ────────────────────────────────────

/** Smoothly counts from 0 to `target` over `duration` seconds. */
export function useCountUp(target: number, duration = 1.4, delay = 0.6) {
  const motionVal = useMotionValue(0);
  const rounded = useTransform(motionVal, v => Math.round(v));
  useEffect(() => {
    const controls = animate(motionVal, target, {
      duration,
      delay,
      ease: 'easeOut'
    });
    return controls.stop;
  }, [target, duration, delay, motionVal]);
  return rounded;
}